import type {
  AreaId,
  EmergencyContactId,
  PersonId,
  SubAreaId,
} from '@shared/brand';
import type { CalendarDate } from '../value-objects/CalendarDate';
import type { ParentRole, PersonStatus, Role, ShirtSize, WorkMode } from '../vocabulary';
import type { Auditable } from './Organization';

/**
 * A person in the organisation.
 *
 * Note what is *not* here: the list of their leaders. The prototype embeds it as
 * `leads: ['Ana Cifuentes', 'Rocío Paz']`, and that array is the single biggest
 * obstacle to the relational migration — a many-to-many relation cannot be
 * normalised out of an array, and it also caps a person's leader count at
 * whatever fits in a document. It lives in `PersonLeaderLink` instead, one row
 * per pair, which is a junction table in Postgres and an indexed collection in
 * Firestore.
 *
 * Balances are likewise absent: they are derived, they change without the person
 * changing, and recomputing them on every read of a 500-person roster would be
 * the app's dominant cost. They live in their own precomputed records.
 */
export interface Person extends Auditable {
  readonly id: PersonId;
  /** Firebase Auth uid. Null for a record created by HR before first sign-in. */
  readonly uid: string | null;
  readonly email: string;
  readonly displayName: string;
  /**
   * Las cuatro partes con que se capturó el nombre, cuando se capturó así.
   *
   * `null` en todo registro anterior al alta estructurada, y eso es válido para
   * siempre: no se rellena adivinando. «José Carlos Lugardo Ramírez» y «María de
   * los Ángeles Ríos» se parten distinto y ninguna heurística acierta las dos.
   *
   * `displayName` sigue siendo lo que se muestra —está desnormalizado en varias
   * colecciones para evitar N+1— y estas partes son la entrada que lo compone.
   * Ver `PersonFullName`.
   */
  readonly nameParts: PersonNameParts | null;
  /**
   * Su puesto, del catálogo de `config/catalogs`.
   *
   * `null` en todo registro anterior a que el catálogo existiera. No se rellena
   * adivinando desde la sub área: «Backend» es dónde trabaja alguien, no qué hace.
   */
  readonly positionId: string | null;
  readonly areaId: AreaId;
  /** Null while a newly validated account still needs a sub area. */
  readonly subAreaId: SubAreaId | null;
  readonly role: Role;
  /**
   * Lo que esta persona puede **además** de lo que da su rol.
   *
   * El rol reparte el paquete de un trabajo y sirve para casi todo el mundo; lo
   * que no sabía decir era la excepción, que en 338 personas es constante:
   * «RR.HH., y además ve sueldos». Antes la única forma de concederlo era ampliar
   * el rol, y ampliarlo se lo amplía a todos los que lo tienen.
   *
   * Vacío es lo normal y significa «lo que diga su rol».
   */
  /**
   * Cómo trabaja: presencial, híbrido o remoto.
   *
   * `null` en todo registro anterior a que el campo existiera, y eso es válido
   * para siempre: no se rellena adivinando. La ficha lo enseña como «Sin
   * registrar», que es información — dice a quién falta preguntarle.
   */
  readonly workMode: WorkMode | null;
  /**
   * Qué equipo se le entregó. Texto libre a propósito.
   *
   * Las respuestas reales son «MacBook Pro 14"» y «Dell Latitude 5420», y un
   * catálogo obligaría a mantener una lista de modelos que cambia cada compra. Lo
   * que hace falta el día de la baja es saber qué hay que recoger.
   */
  readonly computerEquipment: string;
  /**
   * Los documentos del expediente que ya se subieron, por tipo.
   *
   * Guarda la **ruta** de Storage y no una URL: las de descarga caducan, y una
   * ficha que guardara una se quedaría con un enlace muerto sin avisar. La URL se
   * pide en el momento de abrirlo, para quien lo abre.
   *
   * Vacío es lo normal en todo registro anterior a que el expediente se subiera
   * al portal — 326 personas lo tienen hoy en Drive.
   */
  readonly documents: Readonly<Record<string, PersonDocumentRef>>;
  readonly extraPermissions: readonly string[];
  /**
   * Lo que se le quitó de lo que su rol le daba.
   *
   * La otra mitad, y hace falta por el mismo motivo al revés: «administradora de
   * área, pero no expedientes». Quitar gana sobre conceder — ver
   * `effectivePermissions`.
   */
  readonly revokedPermissions: readonly string[];
  /**
   * `pending` until an area administrator validates the account. A pending
   * person can sign in and sees nothing — no role claim is minted, so the
   * security rules deny every read.
   */
  readonly status: PersonStatus;
  /** Drives the seniority table that determines entitlement. */
  readonly hireDate: CalendarDate | null;
  /**
   * Cuándo nació. Vive aquí y no en el expediente, y es una decisión.
   *
   * `PersonalRecord` está cerrado por reglas a la propia persona, al superusuario
   * y a quien administra su área — a propósito, para que un líder no lea el
   * expediente de su equipo entero. Pero el uso de esta fecha es felicitar, y una
   * felicitación que sólo pueden ver tres personas no es una felicitación. Por eso
   * está en la ficha, que su área sí lee.
   *
   * Lo que se guarda es la fecha completa porque es la que da RR.HH. y la que se
   * corrige; lo que se enseña en un cumpleaños es el día y el mes. Que el año esté
   * aquí significa que el área lo ve: si algún día eso incomoda, la fecha se muda
   * al expediente y aquí se queda un `birthday` de mes y día. Hoy no se hace,
   * porque partir el dato en dos sitios es exactamente cómo se acaba con dos
   * versiones y una vieja.
   *
   * `null` en 8 de las 338 filas del padrón de RR.HH., y no se adivina.
   */
  readonly birthDate: CalendarDate | null;
  /**
   * La talla de playera, para la ropa que reparte la empresa.
   *
   * `null` en 50 de 338 —Administración y Finanzas entera no la tiene— y eso es
   * información: dice a quién preguntarle. Ver `parseShirtSize`, que es lo que
   * traduce lo escrito a mano.
   */
  readonly shirtSize: ShirtSize | null;
  /**
   * Si es papá o mamá. Ver `PARENT_ROLES`: no es un dato de género.
   *
   * La cuenta de hijos no está aquí —vive en `PersonalRecord.children`, que es
   * expediente— porque para felicitar basta con saber que lo es.
   */
  readonly parentRole: ParentRole | null;
  /**
   * Días por contrato, cuando la tabla de antigüedad no los produce.
   *
   * `null` —el caso de casi todo el mundo— significa «lo que diga la antigüedad».
   * El caso vivo es el CTO, con 28 días que ninguna antigüedad da; la alternativa
   * era falsearle la fecha de ingreso, y eso habría dejado mal su aniversario y su
   * fecha de liberación para comprar una cifra.
   */
  readonly entitledDaysOverride: number | null;
  readonly photoURL: string | null;
  readonly notificationPrefs: NotificationPreferences;
  /**
   * La baja, cuando la hay.
   *
   * `status: 'inactive'` ya existía y es un enum que alguien cambiaba a mano: no
   * decía **cuándo** ni **por qué**, así que una persona desactivada por error era
   * indistinguible de una que se fue, y nadie podía contestar «¿desde cuándo?».
   *
   * Va aquí y no en una colección aparte porque es un hecho sobre la persona y
   * hay como mucho uno vigente. Un historial de altas y bajas —alguien que se va y
   * regresa— es otra cosa y pediría su propia colección; hoy no existe ese caso.
   */
  readonly termination: Termination | null;
}

/**
 * Por qué y desde cuándo dejó de estar activa una persona.
 *
 * **Registrarla no es todo lo que una baja debe disparar.** Faltan cerrar su
 * saldo, liberar los liderazgos que ejercía y avisar a quien corresponda, y las
 * tres son de servidor: `demote` ya desvincula reportes y libera sub áreas, y una
 * baja necesita eso y más. Ver `docs/KNPEOPLE.md` § People.
 */
export interface Termination {
  readonly date: CalendarDate;
  /** Del catálogo de motivos, o escrito a mano cuando ninguno encaja. */
  readonly reason: string;
  /** Quién la registró. Una baja sin autor no es evidencia de nada. */
  readonly byPersonId: PersonId;
}

/**
 * El nombre tal como se capturó, guardado sin recomponer.
 *
 * Las dos opcionales son `null` y no ausentes: quien no tiene segundo apellido
 * —extranjeros, sobre todo— es distinto de quien lo tiene y nadie lo escribió.
 */
export interface PersonNameParts {
  readonly primerNombre: string;
  readonly segundoNombre: string | null;
  readonly primerApellido: string;
  readonly segundoApellido: string | null;
}

/** The handoff's "Avisos" tab. The only part of a person a client may write. */
export interface NotificationPreferences {
  readonly email: boolean;
  readonly slack: boolean;
  readonly reminder: boolean;
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  email: true,
  slack: true,
  reminder: false,
};

/**
 * One person-reports-to-leader edge. The junction that replaces the embedded
 * array; `id` is the deterministic `${personId}_${leaderPersonId}` so the same
 * pair can never be linked twice.
 */
export interface PersonDocumentRef {
  readonly storagePath: string;
  readonly uploadedAt: Date | null;
  readonly uploadedByPersonId: string | null;
}

export interface PersonLeaderLink extends Auditable {
  readonly id: string;
  readonly personId: PersonId;
  readonly leaderPersonId: PersonId;
  /** Denormalised so the calendar and the roster can scope without a join. */
  readonly areaId: AreaId;
  /**
   * What this leader leads the person *on* — «App 4.2 iOS», «Migración Swift 6».
   *
   * Load-bearing for the double sign-off, not decoration: two signatures are
   * required precisely because the person works across two projects, and the
   * approval card has to say which is which or the second signature looks like
   * a duplicate of the first.
   */
  readonly projectLabel: string;
}

export const personLeaderLinkId = (personId: PersonId, leaderPersonId: PersonId): string =>
  `${personId}_${leaderPersonId}`;

/**
 * An emergency contact. Third-party personal data — a relative's phone number,
 * given by the employee, about someone who never used this system. It is read
 * behind the `ver_sos` permission, the storage rules restrict it to the owner
 * and their managers, and it is never included in an export.
 */
export interface EmergencyContact extends Auditable {
  readonly id: EmergencyContactId;
  readonly personId: PersonId;
  readonly areaId: AreaId;
  readonly name: string;
  readonly relation: string;
  readonly phone: string;
  /** Ascending; 0 is "Hacer primero". Reordering rewrites these. */
  readonly preferenceOrder: number;
}

/** The handoff asks for at least two contacts and warns when one is missing. */
export const MINIMUM_EMERGENCY_CONTACTS = 2;

/**
 * Lo que una carga masiva tiene permitido escribir en una ficha. Tres campos.
 *
 * **El resto de la ficha ya está en el servidor y una carga no es quien para
 * tocarlo.** El puesto, el área, la sub área, el correo, el rol, el estado y la
 * fecha de ingreso los pusieron ahí un alta, una validación o un traslado, cada
 * uno con su rastro; el libro de RR.HH. trae versiones distintas de varios —siete
 * personas con otra área, y un puesto que no coincide con el del catálogo— y
 * decidir cuál gana es trabajo de quien administra el padrón, no de un guion que
 * corre a las once de la noche.
 *
 * Que esta lista exista y no sea un comentario es el punto: `complementPatch` no
 * puede devolver otra cosa, así que «esta carga sólo complementa» deja de ser una
 * promesa de la cabecera y pasa a ser algo que el compilador sostiene.
 */
export const BULK_LOADABLE_FIELDS = ['birthDate', 'shirtSize', 'parentRole'] as const;
export type BulkLoadableField = (typeof BULK_LOADABLE_FIELDS)[number];

/**
 * Los tres campos tal y como viajan a Firestore: la fecha como cadena ISO.
 *
 * Se trabaja sobre la forma almacenada y no sobre la entidad porque quien llama
 * es una carga que lee y escribe documentos, y convertir a `CalendarDate` para
 * volver a convertir sólo añadiría un sitio donde equivocarse.
 */
export interface PersonComplement {
  readonly birthDate: string | null;
  readonly shirtSize: ShirtSize | null;
  readonly parentRole: ParentRole | null;
}

/**
 * Qué hay que escribir para complementar una ficha. Nunca nada más, y nunca un
 * borrado.
 *
 * Dos reglas, y las dos son «complementar» y no «sustituir»:
 *
 * - **Un hueco en el libro no vacía la ficha.** `null` en la entrada significa
 *   «el libro no lo trae», que no es lo mismo que «esta persona no lo tiene».
 *   47 filas vienen sin talla y 8 sin fecha; si un `null` se escribiera, la
 *   segunda corrida de esta carga borraría lo que la primera puso.
 * - **Lo que ya coincide no se escribe.** Es lo que la hace idempotente: correrla
 *   dos veces deja el mismo documento y la segunda vez no mueve ni `updatedAt`.
 *
 * Un valor distinto del que ya hay **sí** se pisa, y eso es deliberado: el libro
 * es la fuente de estos tres campos, así que una corrección en el libro tiene que
 * poder llegar. Lo que no puede llegar es un hueco.
 */
export const complementPatch = (
  actual: PersonComplement,
  entrante: PersonComplement,
): Partial<PersonComplement> => {
  /* El tipo mapeado quita el `readonly` y conserva el de cada campo, que un
     `Record<string, string>` ensancharía a `string` y obligaría a una aserción. */
  const patch: { -readonly [K in BulkLoadableField]?: PersonComplement[K] } = {};

  /* Campo por campo y no en bucle: en un bucle, `entrante[campo]` es la unión de
     los tres tipos y no hay forma de asignarlo sin una aserción, que es
     exactamente la clase de escape que esta función existe para no necesitar.
     Que los tres sigan siendo tres lo vigila la prueba «sólo puede devolver los
     tres campos», que compara las llaves contra `BULK_LOADABLE_FIELDS`. */
  const { birthDate, shirtSize, parentRole } = entrante;
  if (birthDate !== null && birthDate !== actual.birthDate) patch.birthDate = birthDate;
  if (shirtSize !== null && shirtSize !== actual.shirtSize) patch.shirtSize = shirtSize;
  if (parentRole !== null && parentRole !== actual.parentRole) patch.parentRole = parentRole;

  return patch;
};

export const hasCompleteEmergencyContacts = (contacts: readonly EmergencyContact[]): boolean =>
  contacts.length >= MINIMUM_EMERGENCY_CONTACTS;

/**
 * Initials for the avatar. Takes the first letter of the first two words, which
 * for Spanish names ("Ana Cifuentes") gives the expected "AC" and degrades
 * sensibly for a single-word or empty name rather than throwing.
 */
export const initialsOf = (displayName: string): string => {
  const words = displayName.trim().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
    .toUpperCase();
};

/** Whether this person may act at all — the gate every use case checks first. */
export const isActivePerson = (person: Pick<Person, 'status'>): boolean =>
  person.status === 'active';
